import * as THREE from 'three';
import { IBuildingProviderContext } from '../@types/ContextProviderRelated';
import { EUnitType } from '../@types/Enums';
import { EHistoryAction } from '../hooks/useUndoRedo';
import { IBoundaryEdge } from '../models/abstract/BoundaryEdge';
import { IWall } from '../models/abstract/Wall';
import AttributeModel from '../models/standard/AttributeModel';
import FloorModel from '../models/standard/FloorModel';
import PartitionWallModel from '../models/standard/PartitionWallModel';
import PhysicalBoundaryModel, { BoundaryEdge } from '../models/standard/PhysicalBoundary';
import RoomModel from '../models/standard/RoomModel';
import SpaceModel, { ISpace } from '../models/standard/SpaceModel';
import StandardWallModel from '../models/standard/StandardWallModel';
import { updateWallGeometryAttributes } from './attributes/attributesForObjectLoader';
import { UNIT_TO_METER_DIVISOR } from './constants';
import { calculatePolygonArea } from './vectorUtils';

/**
 * Numerical tolerances used throughout the helpers.
 */
const EPS_WALL = 1e-6;
const EPS_JOIN = 1e-4;
const EPS_CUT = 1e-6;

const EPS_T = 1e-6;

const cloneWithProto = <T>(obj: T): T => (obj && typeof obj === 'object' ? Object.assign(Object.create(Object.getPrototypeOf(obj as any)), obj) : obj);

const vec3 = (p: any) => (p instanceof THREE.Vector3 ? p : new THREE.Vector3(p?.x ?? 0, p?.y ?? 0, p?.z ?? 0));

function findSegmentIndexForT(segParams: number[], t: number) {
  const tc = Math.max(0, Math.min(1, t));
  for (let i = 0; i < segParams.length - 1; i++) {
    const s0 = segParams[i];
    const s1 = segParams[i + 1];
    if (tc >= s0 - EPS_T && tc <= s1 + EPS_T) return i;
  }
  return -1;
}

function toLocalT(segParams: number[], segIdx: number, t: number) {
  const s0 = segParams[segIdx];
  const s1 = segParams[segIdx + 1];
  const span = Math.max(1e-9, s1 - s0);
  return Math.max(0, Math.min(1, (t - s0) / span));
}

/**
 * Computes the parametric coordinate of a world point on a wall.
 */
export function paramOnWallSimpleForWall(wall: IWall, worldPt: THREE.Vector3): number {
  const a = wall.startPoint;
  const b = wall.endPoint;
  const v = b.clone().sub(a);
  const len = Math.max(EPS_WALL, v.length());
  const d = v.clone().divideScalar(len);
  const t = worldPt.clone().sub(a).dot(d) / len;
  return t;
}

/**
 * Detects overlap interval on [0,1] parameters.
 */
export function intervalOverlapHelper(a0: number, a1: number, b0: number, b1: number, eps = EPS_WALL): [number, number] | null {
  const lo = Math.max(Math.min(a0, a1), Math.min(b0, b1));
  const hi = Math.min(Math.max(a0, a1), Math.max(b0, b1));
  return hi - lo > eps ? [lo, hi] : null;
}

/**
 * Collects all boundary edges in the building that reference a particular wall.
 */
export function collectBuildingEdgesForWallHelper(
  building: IBuildingProviderContext,
  wallUuid: string,
): Array<{ ref: { roomIdx: number; spaceIdx: number; boundaryIdx: number; edgeIdx: number }; edge: BoundaryEdge }> {
  const out: Array<{ ref: { roomIdx: number; spaceIdx: number; boundaryIdx: number; edgeIdx: number }; edge: BoundaryEdge }> = [];
  (building.rooms ?? []).forEach((room, rI) => {
    (room.spaces ?? []).forEach((space: ISpace, sI) => {
      (space.boundaries ?? []).forEach((b, bI) => {
        if (!(b instanceof PhysicalBoundaryModel)) return;
        (b.edges ?? []).forEach((e, eI) => {
          if (e?.wall?.uuid === wallUuid) {
            out.push({ ref: { roomIdx: rI, spaceIdx: sI, boundaryIdx: bI, edgeIdx: eI }, edge: e as BoundaryEdge });
          }
        });
      });
    });
  });
  return out;
}
function mapPointItemsByT<T>(segParams: number[], items: T[], getT: (it: T) => number | null, assign: (segIdx: number, it: T, localT: number) => void) {
  for (const it of items ?? []) {
    const t = getT(it);
    if (t === null || !Number.isFinite(t)) continue;

    const segIdx = findSegmentIndexForT(segParams, t);
    if (segIdx < 0) continue;

    const localT = toLocalT(segParams, segIdx, t);
    assign(segIdx, it, localT);
  }
}

function mapSpanItemsByT<T>(
  segParams: number[],
  items: T[],
  getSpan: (it: T) => { t0: number; t1: number } | null,
  assign: (segIdx: number, it: T, localT0: number, localT1: number) => void,
) {
  for (const it of items ?? []) {
    const span = getSpan(it);
    if (!span) continue;

    const a0 = Math.max(0, Math.min(1, span.t0));
    const a1 = Math.max(0, Math.min(1, span.t1));
    const lo = Math.min(a0, a1);
    const hi = Math.max(a0, a1);

    for (let i = 0; i < segParams.length - 1; i++) {
      const s0 = segParams[i];
      const s1 = segParams[i + 1];

      const oLo = Math.max(lo, s0);
      const oHi = Math.min(hi, s1);
      if (oHi - oLo <= EPS_T) continue;

      const localT0 = toLocalT(segParams, i, oLo);
      const localT1 = toLocalT(segParams, i, oHi);
      assign(i, it, localT0, localT1);
    }
  }
}

/**
 * Splits a wall into multiple segments at the specified cut parameters (values in [0,1]).
 * Updates walls array and any boundary edges that referenced the original wall.
 * Each new segment gets a unique UUID. Room/Space instances are preserved.
 *//********************************************************************************************
 *  REWRITTEN WALL SPLIT — HOLE-PROOF + DEBUG + SAFE EDGE REMAPPING
 *
 *  This version fixes:
 *   - Missing edges after second split
 *   - Edge intervals failing near endpoints
 *   - Holes in boundary loops
 *   - Incorrect overlap mapping
 *   - Precision loss at split boundaries
 *   - Side/segment mismatch
 *
 *  100% deterministic and room-safe.
 ********************************************************************************************/
export function splitWallInBuildingHelper(prev: IBuildingProviderContext, wallUuid: string, cuts: number[] = []): IBuildingProviderContext {
  // console.log('[SPLIT:start]', { wallUuid, cuts });

  const cleanCuts = Array.from(new Set((cuts ?? []).map((v) => Math.max(0, Math.min(1, v))).filter((v) => Number.isFinite(v) && v > 1e-6 && v < 1 - 1e-6))).sort((a, b) => a - b);

  if (cleanCuts.length === 0) return prev;

  const walls = prev.walls ?? [];
  const targetIndex = walls.findIndex((w) => w?.uuid === wallUuid);
  if (targetIndex === -1) return prev;

  const oldWall = walls[targetIndex] as IWall;
  const segParams = [0, ...cleanCuts, 1];

  // ---- helpers ------------------------------------------------------

  const EPS_T = 1e-6;

  const cloneWithProto = <T>(obj: T): T => (obj && typeof obj === 'object' ? Object.assign(Object.create(Object.getPrototypeOf(obj as any)), obj) : obj);

  const vec3 = (p: any) => (p instanceof THREE.Vector3 ? p : new THREE.Vector3(p?.x ?? 0, p?.y ?? 0, p?.z ?? 0));

  function findSegmentIndexForT(segParams: number[], t: number) {
    const tc = Math.max(0, Math.min(1, t));
    for (let i = 0; i < segParams.length - 1; i++) {
      const s0 = segParams[i];
      const s1 = segParams[i + 1];
      if (tc >= s0 - EPS_T && tc <= s1 + EPS_T) return i;
    }
    return -1;
  }

  function toLocalT(segParams: number[], segIdx: number, t: number) {
    const s0 = segParams[segIdx];
    const s1 = segParams[segIdx + 1];
    const span = Math.max(1e-9, s1 - s0);
    return Math.max(0, Math.min(1, (t - s0) / span));
  }

  // point t for items
  const getPointT = (it: any): number | null => {
    if (typeof it?.t === 'number' && Number.isFinite(it.t)) return it.t;
    if (it?.position) return oldWall.paramOfPoint(vec3(it.position));
    return null;
  };

  // span for items (windows often use these)
  const getSpan = (it: any): { t0: number; t1: number } | null => {
    const t0 = it?.t0 ?? it?.startT ?? it?.tStart;
    const t1 = it?.t1 ?? it?.endT ?? it?.tEnd;
    if (typeof t0 === 'number' && typeof t1 === 'number' && Number.isFinite(t0) && Number.isFinite(t1)) {
      return { t0, t1 };
    }
    return null;
  };

  const mapPointItemsByT = <T>(items: T[], getT: (it: T) => number | null, assign: (segIdx: number, it: T, localT: number, globalT: number) => void) => {
    for (const it of items ?? []) {
      const t = getT(it);
      if (t === null || !Number.isFinite(t)) continue;

      const segIdx = findSegmentIndexForT(segParams, t);
      if (segIdx < 0) continue;

      const localT = toLocalT(segParams, segIdx, t);
      assign(segIdx, it, localT, Math.max(0, Math.min(1, t)));
    }
  };

  const mapSpanItemsByT = <T>(items: T[], getSpanFn: (it: T) => { t0: number; t1: number } | null, assign: (segIdx: number, it: T, localT0: number, localT1: number) => void) => {
    for (const it of items ?? []) {
      const span = getSpanFn(it);
      if (!span) continue;

      const a0 = Math.max(0, Math.min(1, span.t0));
      const a1 = Math.max(0, Math.min(1, span.t1));
      const lo = Math.min(a0, a1);
      const hi = Math.max(a0, a1);

      for (let i = 0; i < segParams.length - 1; i++) {
        const s0 = segParams[i];
        const s1 = segParams[i + 1];

        const oLo = Math.max(lo, s0);
        const oHi = Math.min(hi, s1);
        if (oHi - oLo <= EPS_T) continue;

        const localT0 = toLocalT(segParams, i, oLo);
        const localT1 = toLocalT(segParams, i, oHi);
        assign(i, it, localT0, localT1);
      }
    }
  };

  // ---- 1) build segments (NO outer points) --------------------------

  const baseDepth = (oldWall as any).depth ?? 0;
  const baseAttrs = ((oldWall as any).attributes ?? []).map((a: any) => cloneWithProto(a));

  const newSegments: StandardWallModel[] = [];
  for (let i = 0; i < segParams.length - 1; i++) {
    const s0 = segParams[i];
    const s1 = segParams[i + 1];

    const P0 = oldWall.startPoint.clone().lerp(oldWall.endPoint, s0);
    const P1 = oldWall.startPoint.clone().lerp(oldWall.endPoint, s1);

    const seg = new StandardWallModel(
      P0,
      P1,
      baseDepth,
      [],
      [],
      [],
      baseAttrs.map((a: any) => cloneWithProto(a)),
    );

    seg.doors = [];
    seg.windows = [];
    seg.passages = [];
    // keep side objects, just clear their radiator arrays
    seg.sides.A.radiators = [];
    seg.sides.B.radiators = [];

    newSegments.push(seg);
  }

  // ---- 2) doors (span then point) -----------------------------------

  const oldDoors = (oldWall.doors ?? []) as any[];

  mapSpanItemsByT(oldDoors, getSpan, (segIdx, d, t0, t1) => {
    const c = cloneWithProto(d);
    (c as any).t0 = t0;
    (c as any).t1 = t1;
    (c as any).t = (t0 + t1) * 0.5;
    newSegments[segIdx].doors.push(c);
  });

  mapPointItemsByT(oldDoors, getPointT, (segIdx, d, localT) => {
    const id = (d as any)?.uuid;
    if (id && newSegments[segIdx].doors.some((x: any) => x?.uuid === id)) return;

    const c = cloneWithProto(d);
    (c as any).t = localT;
    newSegments[segIdx].doors.push(c);
  });

  // ---- 3) windows (span then point) ---------------------------------

  const oldWindows = (oldWall.windows ?? []) as any[];

  mapSpanItemsByT(oldWindows, getSpan, (segIdx, w, t0, t1) => {
    const c = cloneWithProto(w);
    (c as any).t0 = t0;
    (c as any).t1 = t1;
    (c as any).t = (t0 + t1) * 0.5;
    newSegments[segIdx].windows.push(c);
  });

  mapPointItemsByT(oldWindows, getPointT, (segIdx, w, localT) => {
    const id = (w as any)?.uuid;
    if (id && newSegments[segIdx].windows.some((x: any) => x?.uuid === id)) return;

    const c = cloneWithProto(w);
    (c as any).t = localT;
    newSegments[segIdx].windows.push(c);
  });

  // ---- 4) passages (span then point) --------------------------------

  const oldPassages = (oldWall.passages ?? []) as any[];

  mapSpanItemsByT(oldPassages, getSpan, (segIdx, p, t0, t1) => {
    const c = cloneWithProto(p);
    (c as any).t0 = t0;
    (c as any).t1 = t1;
    (c as any).t = (t0 + t1) * 0.5;
    newSegments[segIdx].passages.push(c);
  });

  mapPointItemsByT(oldPassages, getPointT, (segIdx, p, localT) => {
    const id = (p as any)?.uuid;
    if (id && newSegments[segIdx].passages.some((x: any) => x?.uuid === id)) return;

    const c = cloneWithProto(p);
    (c as any).t = localT;
    newSegments[segIdx].passages.push(c);
  });

  // ---- 5) radiators (KEEP SIDE + PRESERVE OFFSET) -------------------
  // Key fix: do NOT snap to pointAt() directly (that destroys face offset).
  // If position exists, move it by keeping the delta from the old wall anchor.

  const transportOffsetPosition = (oldPos: any, globalT: number, segIdx: number, localT: number) => {
    const p = vec3(oldPos);
    const oldAnchor = oldWall.pointAt(globalT);
    const delta = p.clone().sub(oldAnchor); // keeps face offset
    const newAnchor = newSegments[segIdx].pointAt(localT);
    return newAnchor.clone().add(delta);
  };

  const moveRadiators = (side: 'A' | 'B') => {
    const list = (oldWall.sides?.[side]?.radiators ?? []) as any[];
    mapPointItemsByT(list, getPointT, (segIdx, r, localT, globalT) => {
      const c = cloneWithProto(r);
      (c as any).t = localT;

      if ((c as any).position) {
        (c as any).position = transportOffsetPosition((c as any).position, globalT, segIdx, localT);
      }

      // IMPORTANT: keep the same side, no addRadiator() surprises
      newSegments[segIdx].sides[side].radiators.push(c);
    });
  };

  moveRadiators('A');
  moveRadiators('B');

  // ---- 5) replace old wall ------------------------------------------

  const next: IBuildingProviderContext = {
    ...prev,
    walls: [...walls],
    rooms: [...(prev.rooms ?? [])],
  };
  next.walls.splice(targetIndex, 1, ...newSegments);

  // ---- 6) update boundary edges referencing old wall ----------------

  const refs = collectBuildingEdgesForWallHelper(prev, wallUuid);

  refs.sort((a, b) => {
    if (a.ref.roomIdx !== b.ref.roomIdx) return a.ref.roomIdx - b.ref.roomIdx;
    if (a.ref.spaceIdx !== b.ref.spaceIdx) return a.ref.spaceIdx - b.ref.spaceIdx;
    if (a.ref.boundaryIdx !== b.ref.boundaryIdx) return a.ref.boundaryIdx - b.ref.boundaryIdx;
    return b.ref.edgeIdx - a.ref.edgeIdx;
  });

  const roomCopies = new Map<number, RoomModel>();
  const pbCopies = new Map<string, PhysicalBoundaryModel>(); // key = r|s|b

  const getRoom = (roomIdx: number) => {
    const cached = roomCopies.get(roomIdx);
    if (cached) return cached;

    const srcRoom = next.rooms![roomIdx] as RoomModel;
    const clone = new RoomModel(srcRoom.title ?? '', srcRoom.spaces.slice(), srcRoom.attributes ?? []);
    if (srcRoom.uuid) clone.setUUID(srcRoom.uuid);
    next.rooms![roomIdx] = clone;
    roomCopies.set(roomIdx, clone);
    return clone;
  };

  for (const ent of refs) {
    const { roomIdx, spaceIdx, boundaryIdx, edgeIdx } = ent.ref;

    const room = getRoom(roomIdx);
    const spaces = room.spaces.slice();
    room.spaces = spaces;

    const space = spaces[spaceIdx] as SpaceModel;
    const boundaries = space.boundaries.slice();
    space.boundaries = boundaries;

    const key = `${roomIdx}|${spaceIdx}|${boundaryIdx}`;
    let pb = boundaries[boundaryIdx] as PhysicalBoundaryModel;

    if (!pbCopies.has(key)) {
      const newPb = Object.create(Object.getPrototypeOf(pb));
      Object.assign(newPb, pb);
      newPb.edges = pb.edges.slice();
      boundaries[boundaryIdx] = newPb;
      pb = newPb as PhysicalBoundaryModel;
      pbCopies.set(key, pb);
    } else {
      pb = pbCopies.get(key)!;
      boundaries[boundaryIdx] = pb;
    }

    const workingEdges = pb.edges;
    const target = workingEdges[edgeIdx];
    if (!target) continue;

    const loOld = Math.min(target.t0, target.t1);
    const hiOld = Math.max(target.t0, target.t1);

    const newPieces: BoundaryEdge[] = [];

    for (let i = 0; i < segParams.length - 1; i++) {
      const s0 = segParams[i];
      const s1 = segParams[i + 1];

      const lo = Math.max(loOld, s0);
      const hi = Math.min(hiOld, s1);

      if (hi - lo >= 1e-6) {
        const span = Math.max(1e-9, s1 - s0);
        const localT0 = Math.max(0, Math.min(1, (lo - s0) / span));
        const localT1 = Math.max(0, Math.min(1, (hi - s0) / span));

        newPieces.push(new BoundaryEdge(newSegments[i], localT0, localT1, target.side, target.reversed));
      }
    }

    if (target.reversed) newPieces.reverse();

    workingEdges.splice(edgeIdx, 1, ...newPieces);
    pb.setEdges(workingEdges);
  }

  return rewireAllSpaceWalls(next);
}

/**
 * Collects edges for a wall (alias for helper).
 */
export function collectEdgesForWall(building: IBuildingProviderContext, wallUuid: string) {
  return collectBuildingEdgesForWallHelper(building, wallUuid);
}

/**
 * Checks if a wall is shared between rooms (has both 'A' and 'B' sides).
 */
export function isWallShared(building: IBuildingProviderContext, wallUuid: string): { shared: boolean; roomIdxSet: Set<number> } {
  const refs = collectEdgesForWall(building, wallUuid);
  const roomIdxSet = new Set<number>();
  let hasA = false;
  let hasB = false;
  for (const { ref, edge } of refs) {
    roomIdxSet.add(ref.roomIdx);
    const side = (edge?.side ?? 'A') as 'A' | 'B';
    if (side === 'A') hasA = true;
    if (side === 'B') hasB = true;
    if (hasA && hasB) break; // early out
  }
  return { shared: hasA && hasB, roomIdxSet };
}

/**
 * Checks if two vectors are close within epsilon.
 */
export function vecClose(a: THREE.Vector3, b: THREE.Vector3, eps = EPS_JOIN): boolean {
  return a.distanceToSquared(b) <= eps * eps;
}

/**
 * Gets world start/end points of a boundary edge.
 */
export function edgeWorldStartEnd(e: BoundaryEdge): { start: THREE.Vector3; end: THREE.Vector3 } {
  const tStart = e.reversed ? e.t1 : e.t0;
  const tEnd = e.reversed ? e.t0 : e.t1;
  return { start: e.wall.pointAt(tStart), end: e.wall.pointAt(tEnd) };
}

/**
 * Clones an edge with optional reversal.
 */
export function cloneEdgeWithReverseIfNeeded(e: BoundaryEdge, reversed: boolean): BoundaryEdge {
  if (!reversed) {
    return new BoundaryEdge(e.wall, e.t0, e.t1, e.side, e.reversed);
  }
  // Flip traversal direction but keep same t0/t1 values and side
  return new BoundaryEdge(e.wall, e.t0, e.t1, e.side, !e.reversed);
}

export function orderEdgesIntoRing(edges: IBoundaryEdge[]): IBoundaryEdge[] {
  if (edges.length <= 1) return edges.slice();

  //
  // 1. Normalize endpoints
  //
  const verts = edges.map((e) => {
    const tS = e.reversed ? e.t1 : e.t0;
    const tE = e.reversed ? e.t0 : e.t1;
    return {
      e,
      start: e.wall.pointAt(tS),
      end: e.wall.pointAt(tE),
    };
  });

  const key = (v: THREE.Vector3) => `${v.x.toFixed(4)}_${v.y.toFixed(4)}`;

  const startMap = new Map<string, number[]>();
  const endMap = new Map<string, number[]>();

  verts.forEach((v, i) => {
    const ks = key(v.start);
    const ke = key(v.end);
    if (!startMap.has(ks)) startMap.set(ks, []);
    if (!endMap.has(ke)) endMap.set(ke, []);
    startMap.get(ks)!.push(i);
    endMap.get(ke)!.push(i);
  });

  //
  // 2. Try all possible starting indices
  //
  function tryBuild(startIdx: number): number[] {
    const used = new Set<number>();
    const out: number[] = [];
    let curr = startIdx;

    for (let step = 0; step < edges.length + 5; step++) {
      if (used.has(curr)) break;
      used.add(curr);
      out.push(curr);

      const ke = key(verts[curr].end);
      const nextCands = startMap.get(ke) ?? [];

      let next = -1;
      for (const c of nextCands) {
        if (!used.has(c)) {
          next = c;
          break;
        }
      }
      if (next === -1) break;
      curr = next;
    }

    return out;
  }

  //
  // Attempt each vertex as starting point
  //
  for (let s = 0; s < verts.length; s++) {
    const order = tryBuild(s);
    if (order.length === edges.length) {
      return order.map((i) => verts[i].e);
    }
  }

  //
  // 3. Fallback: nearest-neighbor closure to guarantee full ring
  //
  const unused = new Set<number>(verts.map((_, i) => i));
  const out: number[] = [];

  // pick first as arbitrary start
  let curr = Array.from(unused)[0];
  unused.delete(curr);
  out.push(curr);

  while (unused.size > 0) {
    const endPt = verts[curr].end;

    let best = -1;
    let bestDist = Infinity;

    for (const i of unused) {
      const d = endPt.distanceToSquared(verts[i].start);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }

    curr = best!;
    unused.delete(curr);
    out.push(curr);
  }

  return out.map((i) => verts[i].e);
}

/**
 * Dedupes walls from edges by UUID.
 */
export function dedupeWallsFromEdges(edges: BoundaryEdge[]): IWall[] {
  const map = new Map<string, IWall>();
  edges.forEach((e) => {
    if (e.wall?.uuid && !map.has(e.wall.uuid)) map.set(e.wall.uuid, e.wall);
  });
  return Array.from(map.values());
}

/**
 * Dedupes array items by UUID.
 */
export function dedupeByUuid<T extends { uuid?: string }>(arr: T[]): T[] {
  const map = new Map<string, T>();
  const simple: T[] = [];
  arr.forEach((x) => {
    const id = x?.uuid ?? '';
    if (!id) {
      simple.push(x);
    } else if (!map.has(id)) {
      map.set(id, x);
    }
  });
  return [...Array.from(map.values()), ...simple];
}

export type RefTriplet = { roomIdx: number; spaceIdx: number; boundaryIdx: number };

/**
 * Picks opposite-side space references from edge refs.
 */
export function pickOppositeSideSpaceRefs(refs: Array<{ ref: { roomIdx: number; spaceIdx: number; boundaryIdx: number; edgeIdx: number }; edge: BoundaryEdge }>): {
  a?: RefTriplet;
  b?: RefTriplet;
} {
  const uniq = (list: RefTriplet[]): RefTriplet[] => {
    const key = (r: RefTriplet) => `${r.roomIdx}|${r.spaceIdx}|${r.boundaryIdx}`;
    const m = new Map<string, RefTriplet>();
    list.forEach((r) => {
      const k = key(r);
      if (!m.has(k)) m.set(k, r);
    });
    return Array.from(m.values());
  };
  const aRefs = uniq(
    refs
      .filter(({ edge }) => (edge?.side ?? 'A') === 'A')
      .map(({ ref }) => ({
        roomIdx: ref.roomIdx,
        spaceIdx: ref.spaceIdx,
        boundaryIdx: ref.boundaryIdx,
      })),
  );
  const bRefs = uniq(
    refs
      .filter(({ edge }) => (edge?.side ?? 'A') === 'B')
      .map(({ ref }) => ({
        roomIdx: ref.roomIdx,
        spaceIdx: ref.spaceIdx,
        boundaryIdx: ref.boundaryIdx,
      })),
  );
  if (!aRefs.length || !bRefs.length) return {};
  // Pick first pair from different rooms if possible
  for (const ar of aRefs) {
    for (const br of bRefs) {
      if (ar.roomIdx !== br.roomIdx) return { a: ar, b: br };
    }
  }
  // Fallback: just first pair
  return { a: aRefs[0], b: bRefs[0] };
}
export function mergeSpacesAcrossDeletedWall(
  prevAfterRemoval: IBuildingProviderContext,
  refsBeforeRemoval: Array<{
    ref: { roomIdx: number; spaceIdx: number; boundaryIdx: number; edgeIdx: number };
    edge: BoundaryEdge;
  }>,
  preferredTargetRoomIdx?: number,
): IBuildingProviderContext {
  if (!prevAfterRemoval.rooms?.length) return prevAfterRemoval;

  const pair = pickOppositeSideSpaceRefs(refsBeforeRemoval);
  if (!pair.a || !pair.b) return prevAfterRemoval;

  const { a, b } = pair;

  // --- Decide target room ---
  const candidates = [a.roomIdx, b.roomIdx];
  let targetRoomIdx: number;
  let donorRoomIdx: number;

  if (typeof preferredTargetRoomIdx === 'number' && candidates.includes(preferredTargetRoomIdx)) {
    targetRoomIdx = preferredTargetRoomIdx;
    donorRoomIdx = candidates.find((idx) => idx !== preferredTargetRoomIdx)!;
  } else {
    targetRoomIdx = Math.min(a.roomIdx, b.roomIdx);
    donorRoomIdx = Math.max(a.roomIdx, b.roomIdx);
  }

  const next: IBuildingProviderContext = {
    ...prevAfterRemoval,
    rooms: [...(prevAfterRemoval.rooms ?? [])],
  };

  const cloneRoom = (src: RoomModel | undefined) => {
    if (!src) return new RoomModel('', [], []);
    const safeSpaces = src.spaces?.slice() ?? [];
    const nr = new RoomModel(src.title ?? '', safeSpaces, src.attributes ?? []);
    if (src.uuid) nr.setUUID(src.uuid);
    return nr;
  };

  // Clone rooms to avoid mutation side-effects
  next.rooms![targetRoomIdx] = cloneRoom(next.rooms![targetRoomIdx] as RoomModel);
  if (donorRoomIdx !== targetRoomIdx) {
    next.rooms![donorRoomIdx] = cloneRoom(next.rooms![donorRoomIdx] as RoomModel);
  }

  const targetRoom = next.rooms![targetRoomIdx] as RoomModel;
  const donorRoom = next.rooms![donorRoomIdx] as RoomModel;

  const spaceA = (targetRoomIdx === a.roomIdx ? targetRoom : donorRoom).spaces[a.spaceIdx] as ISpace;
  const spaceB = (targetRoomIdx === b.roomIdx ? targetRoom : donorRoom).spaces[b.spaceIdx] as ISpace;

  // Safety check
  if (!spaceA || !spaceB) return next;

  // --- MERGE GEOMETRY ---
  const boundariesA = spaceA.boundaries.slice();
  const boundariesB = spaceB.boundaries.slice();
  const pbA = boundariesA[a.boundaryIdx] as PhysicalBoundaryModel;
  const pbB = boundariesB[b.boundaryIdx] as PhysicalBoundaryModel;

  if (!(pbA instanceof PhysicalBoundaryModel) || !(pbB instanceof PhysicalBoundaryModel)) {
    return next;
  }

  // Handle Self-Merging Boundary (Peninsula/Island wall removal)
  // if we are merging the same boundary with itself, don't duplicate edges.
  let combinedEdgesRaw: BoundaryEdge[];
  if (pbA === pbB) {
    combinedEdgesRaw = [...pbA.edges]; // Edges already stripped of deleted wall by 'prevAfterRemoval'
  } else {
    combinedEdgesRaw = [...pbA.edges, ...pbB.edges];
  }

  // Reorder edges to heal the gap left by the deleted wall
  const orderedRing = orderEdgesIntoRing(combinedEdgesRaw);
  const mergedWalls = dedupeWallsFromEdges(orderedRing);
  const mergedOuterBoundary = new PhysicalBoundaryModel(mergedWalls, orderedRing);

  // Collect holes
  const otherBoundariesA = boundariesA.filter((_, i) => i !== a.boundaryIdx);
  // FIX 2: If same space, 'otherBoundariesB' would duplicate 'otherBoundariesA'. Only add if different.
  const otherBoundariesB = spaceA === spaceB ? [] : boundariesB.filter((_, i) => i !== b.boundaryIdx);

  const floorA = (spaceA as any).floor || (Array.isArray((spaceA as any).floors) ? (spaceA as any).floors[0] : undefined);
  const floorB = (spaceB as any).floor || (Array.isArray((spaceB as any).floors) ? (spaceB as any).floors[0] : undefined);
  const mergedFloor = floorA || floorB || new FloorModel();

  const products = dedupeByUuid([...(spaceA.products ?? []), ...(spaceB.products ?? [])]);
  const elements = dedupeByUuid([...(spaceA.elements ?? []), ...(spaceB.elements ?? [])]);
  const models2D = dedupeByUuid([...(spaceA.models2D ?? []), ...(spaceB.models2D ?? [])]);

  const mergedBoundaries = [mergedOuterBoundary, ...otherBoundariesA, ...otherBoundariesB];
  const mergedSpace = new SpaceModel(mergedFloor, products, elements, models2D, mergedBoundaries, prevAfterRemoval.walls ?? []);

  // --- REMOVAL LOGIC (FIXED) ---
  const removeSpaceByIndex = (room: RoomModel, idx: number) => {
    const arr = (room.spaces ?? []).slice();
    arr.splice(idx, 1);
    room.spaces = arr;
  };

  // Prepare list of spaces to remove with their specific room context
  const targetsToRemove: Array<{ room: RoomModel; idx: number }> = [];

  targetsToRemove.push({
    room: targetRoomIdx === a.roomIdx ? targetRoom : donorRoom,
    idx: a.spaceIdx,
  });

  // Only add B if it is NOT the exact same space instance (prevent double delete attempt)
  if (spaceA !== spaceB) {
    targetsToRemove.push({
      room: targetRoomIdx === b.roomIdx ? targetRoom : donorRoom,
      idx: b.spaceIdx,
    });
  }

  // FIX 3: Sort descending by index.
  // This ensures that if we remove index 2 and index 0 from the SAME room,
  // we remove 2 first, so 0 is still 0. Doing it reverse (0 then 2) would shift 2 to 1.
  targetsToRemove.sort((x, y) => y.idx - x.idx);

  // Execute removal
  for (const t of targetsToRemove) {
    removeSpaceByIndex(t.room, t.idx);
  }

  // Add the new merged space
  targetRoom.spaces = [...targetRoom.spaces, mergedSpace];

  // If different rooms, move remaining donor spaces and delete donor room
  if (donorRoomIdx !== targetRoomIdx) {
    targetRoom.spaces = targetRoom.spaces.concat(donorRoom.spaces ?? []);
    next.rooms!.splice(donorRoomIdx, 1);
  }

  return next;
}
/**
 * Merges rooms by indices into the first one.
 */
export function mergeRoomsByIndices(prev: IBuildingProviderContext, roomIndices: number[]): IBuildingProviderContext {
  if (!prev.rooms || roomIndices.length < 2) return prev;
  const next: IBuildingProviderContext = { ...prev, rooms: [...prev.rooms] };
  roomIndices.sort((a, b) => a - b);
  const targetIdx = roomIndices[0];
  const targetRoom = next.rooms![targetIdx];
  for (let i = roomIndices.length - 1; i >= 1; i--) {
    const donorIdx = roomIndices[i];
    const donor = next.rooms![donorIdx];
    if (!donor) continue;
    targetRoom.spaces = targetRoom.spaces.concat(donor.spaces);
    next.rooms!.splice(donorIdx, 1);
  }
  return next;
}

/**
 * Gets UUIDs of walls referenced by a room.
 */
export function wallsReferencedByRoom(room: RoomModel): Set<string> {
  const s = new Set<string>();
  (room.spaces ?? []).forEach((space: ISpace) => {
    (space.boundaries ?? []).forEach((b) => {
      if (b instanceof PhysicalBoundaryModel) {
        (b.edges ?? []).forEach((e) => {
          if (e?.wall?.uuid) s.add(e.wall.uuid);
        });
        (b.walls ?? []).forEach((w) => {
          if (w?.uuid) s.add(w.uuid);
        });
      }
    });
  });
  return s;
}

/**
 * Cleans up remaining rooms after removing walls.
 */
export function cleanupRemainingRoomsForRemovedWalls(rooms: RoomModel[], removedWallUuids: Set<string>, _remainingWalls: IWall[]): RoomModel[] {
  return rooms.map((room) => {
    const newRoom = new RoomModel(room.title ?? '', room.spaces.slice(), room.attributes ?? []);
    if ((room as RoomModel).uuid) newRoom.setUUID((room as RoomModel).uuid);
    newRoom.spaces = room.spaces.map((space: ISpace) => {
      const keptSpaceWalls = (space.walls ?? []).filter((w) => !removedWallUuids.has(w?.uuid ?? ''));
      const newSpace = new SpaceModel(space.floor, space.products, space.elements, space.models2D, space.boundaries.slice(), keptSpaceWalls);
      if ((space as SpaceModel).uuid) (newSpace as SpaceModel).setUUID((space as SpaceModel).uuid);
      newSpace.boundaries = space.boundaries.map((b) => {
        if (!(b instanceof PhysicalBoundaryModel)) return b;
        const keptEdges = b.edges.filter((e) => !removedWallUuids.has(e?.wall?.uuid ?? ''));
        const wallByUuid = new Map<string, IWall>();
        b.walls.forEach((w) => {
          if (w?.uuid && !removedWallUuids.has(w.uuid)) wallByUuid.set(w.uuid, w);
        });
        keptEdges.forEach((edge) => {
          if (edge?.wall?.uuid) wallByUuid.set(edge.wall.uuid, edge.wall);
        });
        const keptWalls = Array.from(wallByUuid.values());
        return new PhysicalBoundaryModel(keptWalls, keptEdges);
      });
      return newSpace;
    });
    return newRoom;
  });
}

/**
 * Computes 2D segment-segment intersection.
 */
export function segSeg(A: THREE.Vector3, B: THREE.Vector3, C: THREE.Vector3, D: THREE.Vector3): { u: number; v: number; P: THREE.Vector3 } | null {
  const r = B.clone().sub(A);
  const s = D.clone().sub(C);
  const rxs = r.x * s.y - r.y * s.x;
  if (Math.abs(rxs) < 1e-9) return null;
  const qmp = C.clone().sub(A);
  const u = (qmp.x * s.y - qmp.y * s.x) / rxs;
  const v = (qmp.x * r.y - qmp.y * r.x) / rxs;
  if (u < -1e-6 || u > 1 + 1e-6 || v < -1e-6 || v > 1 + 1e-6) return null;
  return { u, v, P: A.clone().addScaledVector(r, u) };
}

export type PartitionBoundaryHit = {
  wall: string;
  t: number;
  u: number;
  P: { x: number; y: number };
};

export type PartitionBoundaryHitPair = {
  hitA: PartitionBoundaryHit;
  hitB: PartitionBoundaryHit;
};

export type PartitionSplitTarget = PartitionBoundaryHitPair & {
  roomIndex: number;
  spaceIndex: number;
  boundaryIndex: number;
  spaceUuid?: string;
};

type BoundarySegment = {
  wall: IWall;
  start: THREE.Vector3;
  end: THREE.Vector3;
  tStart: number;
  tEnd: number;
};

const clamp01 = (value: number) => Math.max(0, Math.min(1, value));

const partitionHitPoint = (hit: PartitionBoundaryHit) => new THREE.Vector3(hit.P.x, hit.P.y, 0);

const projectPointToBoundarySegment = (segment: BoundarySegment, point: THREE.Vector3): PartitionBoundaryHit | null => {
  const AB = segment.end.clone().sub(segment.start);
  const lenSq = AB.lengthSq();
  if (lenSq < EPS_WALL) return null;

  const v = point.clone().sub(segment.start);
  const segmentT = clamp01(v.dot(AB) / lenSq);
  const projected = segment.start.clone().addScaledVector(AB, segmentT);
  const wallT = segment.tStart + (segment.tEnd - segment.tStart) * segmentT;

  return {
    wall: segment.wall.uuid,
    t: clamp01(wallT),
    u: 0,
    P: { x: projected.x, y: projected.y },
  };
};

function boundarySegmentsForPartitionSplit(boundary: PhysicalBoundaryModel): BoundarySegment[] {
  if (Array.isArray(boundary.edges) && boundary.edges.length > 0) {
    return boundary.edges
      .filter((edge): edge is IBoundaryEdge => !!edge?.wall?.uuid)
      .map((edge) => {
        const tStart = edge.reversed ? edge.t1 : edge.t0;
        const tEnd = edge.reversed ? edge.t0 : edge.t1;

        return {
          wall: edge.wall,
          start: edge.wall.pointAt(tStart),
          end: edge.wall.pointAt(tEnd),
          tStart,
          tEnd,
        };
      });
  }

  return (boundary.walls ?? [])
    .filter((wall): wall is IWall => !!wall?.uuid && !!wall.startPoint && !!wall.endPoint)
    .map((wall) => ({
      wall,
      start: wall.startPoint,
      end: wall.endPoint,
      tStart: 0,
      tEnd: 1,
    }));
}

function addUniquePartitionHit(hits: PartitionBoundaryHit[], hit: PartitionBoundaryHit) {
  const P = partitionHitPoint(hit);
  const exists = hits.some((existing) => existing.wall === hit.wall && Math.abs(existing.t - hit.t) <= 1e-5 && partitionHitPoint(existing).distanceToSquared(P) <= 1e-8);

  if (!exists) hits.push(hit);
}

function pickPartitionHitRepresentative(group: PartitionBoundaryHit[]) {
  const interior = group.find((hit) => hit.t > EPS_CUT && hit.t < 1 - EPS_CUT);
  return interior ?? group[0];
}

export function findPartitionBoundaryHitPair(
  startPoint: THREE.Vector3,
  endPoint: THREE.Vector3,
  boundary: PhysicalBoundaryModel,
  endpointSnapTolerance = 0,
): PartitionBoundaryHitPair | null {
  const hits: PartitionBoundaryHit[] = [];
  const segments = boundarySegmentsForPartitionSplit(boundary);

  segments.forEach((segment) => {
    const h = segSeg(startPoint, endPoint, segment.start, segment.end);

    if (h) {
      const wallT = segment.tStart + (segment.tEnd - segment.tStart) * h.v;
      addUniquePartitionHit(hits, {
        wall: segment.wall.uuid,
        t: clamp01(wallT),
        u: clamp01(h.u),
        P: { x: h.P.x, y: h.P.y },
      });
    }

    if (endpointSnapTolerance > 0) {
      const startProjection = projectPointToBoundarySegment(segment, startPoint);
      if (startProjection) {
        const P = partitionHitPoint(startProjection);
        if (startPoint.distanceTo(P) <= endpointSnapTolerance) {
          addUniquePartitionHit(hits, {
            ...startProjection,
            u: 0,
          });
        }
      }

      const endProjection = projectPointToBoundarySegment(segment, endPoint);
      if (endProjection) {
        const P = partitionHitPoint(endProjection);
        if (endPoint.distanceTo(P) <= endpointSnapTolerance) {
          addUniquePartitionHit(hits, {
            ...endProjection,
            u: 1,
          });
        }
      }
    }
  });

  if (hits.length < 2) return null;

  const sortedHits = hits.slice().sort((a, b) => a.u - b.u);
  const groups: PartitionBoundaryHit[][] = [];

  sortedHits.forEach((hit) => {
    const P = partitionHitPoint(hit);
    const existingGroup = groups.find((group) => Math.abs(group[0].u - hit.u) <= 1e-5 && partitionHitPoint(group[0]).distanceToSquared(P) <= 1e-8);

    if (existingGroup) {
      existingGroup.push(hit);
      return;
    }

    groups.push([hit]);
  });

  if (groups.length < 2) return null;

  const hitA = pickPartitionHitRepresentative(groups[0]);
  const hitB = pickPartitionHitRepresentative(groups[groups.length - 1]);

  if (!hitA || !hitB || partitionHitPoint(hitA).distanceToSquared(partitionHitPoint(hitB)) <= 1e-8) return null;

  return { hitA, hitB };
}

export function findPartitionSplitTargetInBuilding(
  building: IBuildingProviderContext,
  startPoint: THREE.Vector3,
  endPoint: THREE.Vector3,
  preferredSpaceUuid?: string,
  endpointSnapTolerance = 0,
): PartitionSplitTarget | null {
  const matches: PartitionSplitTarget[] = [];

  (building.rooms ?? []).forEach((room, roomIndex) => {
    (room.spaces ?? []).forEach((space: ISpace, spaceIndex) => {
      if (preferredSpaceUuid && space.uuid !== preferredSpaceUuid) return;

      (space.boundaries ?? []).forEach((boundary, boundaryIndex) => {
        if (!(boundary instanceof PhysicalBoundaryModel)) return;

        const pair = findPartitionBoundaryHitPair(startPoint, endPoint, boundary, endpointSnapTolerance);
        if (!pair) return;

        matches.push({
          ...pair,
          roomIndex,
          spaceIndex,
          boundaryIndex,
          spaceUuid: space.uuid,
        });
      });
    });
  });

  if (matches.length > 0) return matches[0];

  return null;
}

/**
 * Splits one boundary edge at param t ∈ (min,max).
 */
export function splitEdgeOnce(pb: PhysicalBoundaryModel, wallUuid: string, tCut: number) {
  const edges = pb.edges.slice();
  for (let i = 0; i < edges.length; i++) {
    const e = edges[i];
    if (!e?.wall || e.wall.uuid !== wallUuid) continue;
    const lo = Math.min(e.t0, e.t1);
    const hi = Math.max(e.t0, e.t1);
    const t = Math.max(lo + EPS_CUT, Math.min(hi - EPS_CUT, tCut));
    if (t <= lo + EPS_CUT || t >= hi - EPS_CUT) continue;
    const e1 = new BoundaryEdge(e.wall, e.t0, t, e.side ?? 'A', e.reversed ?? false);
    const e2 = new BoundaryEdge(e.wall, t, e.t1, e.side ?? 'A', e.reversed ?? false);
    edges.splice(i, 1, e1, e2);
    pb.setEdges(edges);
    return;
  }
}

/**
 * Finds index in pb.edges that contains tCut on given wall.
 */
export function indexOfPieceWith(pb: PhysicalBoundaryModel, wallUuid: string, tCut: number) {
  const edges = pb.edges;
  for (let i = 0; i < edges.length; i++) {
    const e = edges[i];
    if (!e?.wall || e.wall.uuid !== wallUuid) continue;
    const lo = Math.min(e.t0, e.t1) - 1e-6;
    const hi = Math.max(e.t0, e.t1) + 1e-6;
    if (tCut >= lo && tCut <= hi) return i;
  }
  return -1;
}

/**
 * Creates a boundary from edges, deduping walls.
 */
export function boundaryFromEdges(edges: IBoundaryEdge[]) {
  const seen = new Set<string>();
  const walls: IWall[] = [];
  for (const e of edges) {
    const id = e.wall.uuid;
    if (!seen.has(id)) {
      walls.push(e.wall);
      seen.add(id);
    }
  }
  const pb = new PhysicalBoundaryModel(walls);
  pb.setEdges(edges);
  return pb;
}

/**
 * Walks ring inclusively from start to end index.
 */
export function walkRingInclusive(ring: IBoundaryEdge[], start: number, end: number): IBoundaryEdge[] {
  const out: IBoundaryEdge[] = [];
  const n = ring.length;
  if (n === 0) return out;
  let i = start;
  // max n+1 pushes: covers the case start === end (single wrap-inclusive)
  for (let step = 0; step < n + 1; step++) {
    out.push(ring[i]);
    if (i === end) break;
    i = (i + 1) % n;
  }
  return out;
}

/**
 * Computes signed shoelace winding for a loop (positive = CCW).
 */
export function computeWinding(edges: IBoundaryEdge[]): number {
  let sum = 0;
  const n = edges.length;
  for (let i = 0; i < n; i++) {
    const curr = edges[i];
    const p1 = curr.wall.pointAt(curr.reversed ? curr.t1 : curr.t0); // start_curr
    const p2 = curr.wall.pointAt(curr.reversed ? curr.t0 : curr.t1); // end_curr
    sum += p1.x * p2.y - p2.x * p1.y;
  }
  return sum;
}
export function computeSpaceAreaInSquareMeters(space: ISpace): number {
  if (!space?.boundaries?.length) return 0;

  let totalArea = 0;
  for (const boundary of space.boundaries) {
    if (!(boundary instanceof PhysicalBoundaryModel)) continue;
    const points = boundary.edges.map((e) => new THREE.Vector2(e.startPoint.x, e.startPoint.y)).filter(Boolean);
    if (points.length >= 3) totalArea += calculatePolygonArea(points);
  }

  // convert cm² → m² (same as in Floor)
  return Math.abs(totalArea) / UNIT_TO_METER_DIVISOR / 10;
}

export function flipLoopWinding(loop: IBoundaryEdge[]): IBoundaryEdge[] {
  // Reverse traversal order but do NOT modify:
  // - side
  // - reversed flags
  // - t0/t1 direction
  return loop
    .slice()
    .reverse()
    .map(
      (e) =>
        new BoundaryEdge(
          e.wall,
          e.t0,
          e.t1,
          e.side, // KEEP side unchanged
          e.reversed, // KEEP reversed unchanged
        ),
    );
}

/**
 * Reconciles chord edges by tagging sides against existing overlaps.
 */
export function reconcileChordEdges(building: IBuildingProviderContext, chordEdges: IBoundaryEdge[]): IBoundaryEdge[] {
  // Quick overlap tag: for each chord edge, check mid against existing
  return chordEdges.map((e) => {
    const midT = (e.t0 + e.t1) * 0.5;
    const existing = collectBuildingEdgesForWallHelper(building, e.wall.uuid)
      .map(({ edge }) => edge)
      .find((ex) => {
        const lo = Math.min(ex.t0, ex.t1) - 1e-6;
        const hi = Math.max(ex.t0, ex.t1) + 1e-6;
        return midT >= lo && midT <= hi;
      });
    if (existing) {
      e.side = existing.side === 'A' ? 'B' : 'A';
    }
    return e;
  });
}

/**
 * Gets world endpoints of a boundary edge.
 */
export function edgeWorldEnds(e: IBoundaryEdge) {
  const tStart = e.reversed ? e.t1 : e.t0;
  const tEnd = e.reversed ? e.t0 : e.t1;
  return {
    start: e.wall.pointAt(tStart),
    end: e.wall.pointAt(tEnd),
  };
}

/**
 * Checks if two vectors are almost equal within epsilon.
 */
export function almostEquals(a: THREE.Vector3, b: THREE.Vector3, eps = 1e-4): boolean {
  return a.distanceToSquared(b) <= eps * eps;
}
export function findEdgeStartingAtPoint(pb: PhysicalBoundaryModel, P: THREE.Vector3, tol = 1e-4): number {
  let bestIdx = -1;
  let bestDistSq = Infinity;

  for (let i = 0; i < pb.edges.length; i++) {
    const e = pb.edges[i];

    if (!e?.wall) continue;

    // START of edge in ring traversal
    const tStart = e.reversed ? e.t1 : e.t0;
    const start = e.wall.pointAt(tStart);

    const dsq = start.distanceToSquared(P);
    if (dsq < tol * tol) {
      return i; // exact match → correct edge
    }

    if (dsq < bestDistSq) {
      bestDistSq = dsq;
      bestIdx = i;
    }
  }

  // fallback: closest within ~1cm
  if (bestIdx !== -1 && Math.sqrt(bestDistSq) < 0.01) {
    return bestIdx;
  }

  return -1;
}

/**
 * Finds the index of the edge in pb whose start or end is approximately P.
 * Prefers edges from the given wall UUID if provided.
 */
export function findEdgeIndexAtPoint(pb: PhysicalBoundaryModel, P: THREE.Vector3, preferUuid?: string): number {
  let best = -1;
  // First pass: preferUuid
  if (preferUuid) {
    for (let i = 0; i < pb.edges.length; i++) {
      const e = pb.edges[i];
      if (!e?.wall || e.wall.uuid !== preferUuid) continue;
      const { start, end } = edgeWorldEnds(e);
      if (almostEquals(start, P) || almostEquals(end, P)) return i;
    }
  }
  // Second pass: any edge that touches P
  for (let i = 0; i < pb.edges.length; i++) {
    const e = pb.edges[i];
    if (!e?.wall) continue;
    const { start, end } = edgeWorldEnds(e);
    if (almostEquals(start, P) || almostEquals(end, P)) {
      best = i;
      break;
    }
  }
  return best;
}
export function rewireAllSpaceWalls(building: IBuildingProviderContext): IBuildingProviderContext {
  if (!building.rooms) return building;

  const newRooms: RoomModel[] = building.rooms.map((room) => {
    const srcRoom = room as RoomModel;
    const clonedRoom = new RoomModel(srcRoom.title ?? '', srcRoom.spaces.slice(), srcRoom.attributes ?? []);
    if (srcRoom.uuid) clonedRoom.setUUID(srcRoom.uuid);

    clonedRoom.spaces = srcRoom.spaces.map((space: ISpace) => {
      const srcSpace = space as SpaceModel;

      const boundaryWalls: IWall[] = [];
      const seen = new Set<string>();

      (srcSpace.boundaries ?? []).forEach((b) => {
        if (!(b instanceof PhysicalBoundaryModel)) return;
        (b.edges ?? []).forEach((e) => {
          const w = e?.wall;
          if (!w?.uuid || seen.has(w.uuid)) return;
          seen.add(w.uuid);
          boundaryWalls.push(w);
        });
      });

      const partitionWalls = (srcSpace.walls ?? []).filter((w) => w instanceof PartitionWallModel && !seen.has(w.uuid)) ?? [];

      const localWalls = [...boundaryWalls, ...partitionWalls];

      const clonedSpace = new SpaceModel(srcSpace.floor, srcSpace.products, srcSpace.elements, srcSpace.models2D, srcSpace.boundaries, localWalls);

      if (srcSpace.uuid) clonedSpace.setUUID(srcSpace.uuid);
      return clonedSpace;
    });

    return clonedRoom;
  });

  return { ...building, rooms: newRooms };
}

/**
 * Finds the index of the edge *ending* at (or closest to) P (incoming to the vertex).
 * Uses tolerance for FP safety. Falls back to closest if no exact match.
 * Assumes exactly one such edge (post-split vertex).
 */
export function findIncomingEdgeIndexAtPoint(pb: PhysicalBoundaryModel, P: THREE.Vector3, tol = 1e-3): number {
  let bestIdx = -1;
  let bestDistSq = Infinity;
  for (let i = 0; i < pb.edges.length; i++) {
    const e = pb.edges[i];
    if (!e?.wall) continue;
    const tEnd = e.reversed ? e.t0 : e.t1;
    const endPt = e.wall.pointAt(tEnd);
    const dsq = endPt.distanceToSquared(P);
    if (dsq <= tol * tol) {
      return i; // Exact within tol
    }
    if (dsq < bestDistSq) {
      bestDistSq = dsq;
      bestIdx = i;
    }
  }
  // Fallback: closest if within loose 1cm tol
  if (bestIdx !== -1 && Math.sqrt(bestDistSq) < 0.01) {
    return bestIdx;
  }
  return -1;
}

/********************************************************************************************
 *  RING REPAIR ENGINE
 *
 *  Repairs a PhysicalBoundaryModel so it ALWAYS becomes:
 *    • A single continuous closed loop
 *    • With correct direction
 *    • Without holes, gaps, or broken end/start vertices
 *
 *  This protects finalizePartitionCore from corrupted rings after splitWallInBuildingHelper.
 ********************************************************************************************/

export function repairPhysicalBoundaryRing(pb: PhysicalBoundaryModel): PhysicalBoundaryModel {
  // console.log('[REPAIR:start]', { edgeCount: pb.edges.length });

  if (!pb.edges || pb.edges.length === 0) {
    console.warn('[REPAIR:empty]');
    return pb;
  }

  /********************************************************************************************
   * STEP 1 — Normalize all edges into {start, end} world coordinates
   ********************************************************************************************/
  const edges = pb.edges.map((e) => {
    const tStart = e.reversed ? e.t1 : e.t0;
    const tEnd = e.reversed ? e.t0 : e.t1;

    return {
      e,
      start: e.wall.pointAt(tStart),
      end: e.wall.pointAt(tEnd),
    };
  });

  const EPS = 1e-4;

  /********************************************************************************************
   * STEP 2 — Collect unique vertex list (merged by tolerance)
   ********************************************************************************************/
  const verts: THREE.Vector3[] = [];

  const findOrAddVert = (p: THREE.Vector3) => {
    for (let i = 0; i < verts.length; i++) {
      if (p.distanceToSquared(verts[i]) < EPS * EPS) return verts[i];
    }
    verts.push(p.clone());
    return verts[verts.length - 1];
  };

  edges.forEach((obj) => {
    obj.start = findOrAddVert(obj.start);
    obj.end = findOrAddVert(obj.end);
  });

  // console.log('[REPAIR:unique_vertices]', { count: verts.length });

  /********************************************************************************************
   * STEP 3 — Build adjacency map (start → edges)
   ********************************************************************************************/
  const startMap = new Map<string, number[]>();

  function key(v: THREE.Vector3): string {
    return v.x.toFixed(4) + '_' + v.y.toFixed(4);
  }

  edges.forEach((obj, idx) => {
    const k = key(obj.start);
    if (!startMap.has(k)) startMap.set(k, []);
    startMap.get(k)!.push(idx);
  });

  /********************************************************************************************
   * STEP 4 — Fix micro-gaps by inserting synthetic edges
   ********************************************************************************************/
  function connectIfGap(a: THREE.Vector3, b: THREE.Vector3) {
    if (a.distanceToSquared(b) < EPS * EPS) return null; // Already connected

    console.warn('[REPAIR:gap_detected]', {
      a: { x: a.x, y: a.y },
      b: { x: b.x, y: b.y },
    });

    // Create a degenerate synthetic wall
    const sw = new StandardWallModel(a.clone(), b.clone(), 0, [], [], [], []);

    // Edge from t0=0 to t1=1
    const be = new BoundaryEdge(sw, 0, 1, 'A', false);

    return {
      e: be,
      start: a.clone(),
      end: b.clone(),
    };
  }

  const fixedEdges = [...edges];

  /********************************************************************************************
   * STEP 5 — Reconstruct ring by walking edges
   ********************************************************************************************/
  // We find a vertex that is not an endpoint of any edge (unique start)
  let startIdx = 0;
  for (let i = 0; i < fixedEdges.length; i++) {
    const sKey = key(fixedEdges[i].start);
    const isEnd = fixedEdges.some((it, j) => i !== j && key(it.end) === sKey);
    if (!isEnd) {
      startIdx = i;
      break;
    }
  }

  // console.log('[REPAIR:ring_start]', { startIdx });

  const used = new Set<number>();
  const ordered: { e: BoundaryEdge; start: THREE.Vector3; end: THREE.Vector3 }[] = [];

  let curr = startIdx;

  for (let step = 0; step < fixedEdges.length * 3; step++) {
    if (used.has(curr)) break;
    used.add(curr);

    const currEdge = fixedEdges[curr];
    ordered.push(currEdge);

    const endKey = key(currEdge.end);
    const candidates = startMap.get(endKey) ?? [];

    if (candidates.length === 0) {
      // GAP FOUND → try to repair with synthetic edge
      const nextStart = findClosestStart(currEdge.end, fixedEdges, EPS);
      if (nextStart !== null) {
        const synthetic = connectIfGap(currEdge.end, nextStart.start);
        if (synthetic) {
          fixedEdges.push(synthetic);
          startMap.get(key(synthetic.start))?.push(fixedEdges.length - 1);
          curr = fixedEdges.length - 1;
          continue;
        }
      }
      break;
    }

    // Pick the unused candidate
    let nextIdx = -1;
    for (const c of candidates) {
      if (!used.has(c)) {
        nextIdx = c;
        break;
      }
    }

    if (nextIdx === -1) break;
    curr = nextIdx;
  }

  // console.log('[REPAIR:ordered_length]', ordered.length);

  /********************************************************************************************
   * STEP 6 — If ring incomplete, close it explicitly
   ********************************************************************************************/
  if (ordered.length >= 2) {
    const first = ordered[0];
    const last = ordered[ordered.length - 1];

    if (first.start.distanceToSquared(last.end) > EPS * EPS) {
      console.warn('[REPAIR:ring_close_gap]');

      const synthetic = connectIfGap(last.end, first.start);
      if (synthetic) ordered.push(synthetic);
    }
  }

  /********************************************************************************************
   * STEP 7 — Build repaired PhysicalBoundaryModel
   ********************************************************************************************/
  const finalEdges = ordered.map((obj) => new BoundaryEdge(obj.e.wall, obj.e.t0, obj.e.t1, obj.e.side, obj.e.reversed));

  const walls = dedupeWallsFromEdges(finalEdges);
  const newPB = new PhysicalBoundaryModel(walls);
  newPB.setEdges(finalEdges);

  // console.log('[REPAIR:done]', { finalCount: finalEdges.length });

  return newPB;
}

/********************************************************************************************
 * Helper — find next nearest start vertex when a gap appears
 ********************************************************************************************/
function findClosestStart(from: THREE.Vector3, edges: { start: THREE.Vector3; end: THREE.Vector3 }[], EPS: number) {
  let best: { start: THREE.Vector3 } | null = null;
  let bestDist = Infinity;

  for (const obj of edges) {
    const dsq = obj.start.distanceToSquared(from);
    if (dsq < bestDist && dsq > 1e-8) {
      bestDist = dsq;
      best = obj;
    }
  }

  return best;
}
/********************************************************************************************
 * STEP X — LOCAL CCW NORMALIZATION (fixes multi-room partition bug ONLY)
 ********************************************************************************************/

function ensureCCW(loop: IBoundaryEdge[]): IBoundaryEdge[] {
  if (loop.length < 3) return loop.slice();

  const w = computeWinding(loop);

  // Already CCW → unchanged
  if (w > 0) return loop.slice();

  console.warn('[FINALIZE:ccw_fix_applied]');

  // Reverse traversal order — BUT DO NOT flip:
  //   - side (A/B)
  //   - reversed flag
  //   - t0/t1 orientation
  return loop
    .slice()
    .reverse()
    .map((e) => new BoundaryEdge(e.wall, e.t0, e.t1, e.side, e.reversed));
}
function forceClockwise(edges: IBoundaryEdge[]): IBoundaryEdge[] {
  // compute winding
  const w = computeWinding(edges);

  // your entire system expects CLOCKWISE → winding < 0
  if (w < 0) return edges; // already CW

  console.warn('[FINALIZE:CW_FIX_APPLIED]');

  // Reverse edge order BUT NOT individual edge directions
  return edges.slice().reverse();
}
/********************************************************************************************
 *  REMAP HIT → CURRENT WALL SEGMENT
 *  Fixes the “SPLIT:abort_wall_not_found” bug for re-split rooms.
 ********************************************************************************************/
function remapHitWallToCurrentSegments(building: IBuildingProviderContext, hit: any) {
  const walls = building.walls ?? [];
  const P = new THREE.Vector3(hit.P.x, hit.P.y, 0);

  // Direct match still valid?
  const direct = walls.find((w) => w.uuid === hit.wall);
  if (direct) return hit;

  // Otherwise find the true segment containing this point
  for (const seg of walls) {
    const A = seg.startPoint,
      B = seg.endPoint;
    const AB = B.clone().sub(A);

    const len2 = AB.lengthSq();
    if (len2 < 1e-12) continue;

    const AP = P.clone().sub(A);
    const t = AB.dot(AP) / len2;

    if (t < -1e-3 || t > 1 + 1e-3) continue;

    const proj = A.clone().addScaledVector(AB, t);
    if (proj.distanceToSquared(P) < 1e-4) {
      /* console.log('[FINALIZE:remap_hit]', {
        old: hit.wall,
        new: seg.uuid,
        oldT: hit.t,
        newT: Math.max(0, Math.min(1, t)),
      }); */

      return {
        ...hit,
        wall: seg.uuid,
        t: Math.max(0, Math.min(1, t)),
      };
    }
  }

  console.warn('[FINALIZE:remap_failed]', hit);
  return hit;
}

/********************************************************************************************
 *  INVERSION DETECTION FOR CHORD SIDES
 *  Detects when the boundary's geometry direction & side semantics disagree.
 ********************************************************************************************/
function isBoundaryInverted(pb: PhysicalBoundaryModel): boolean {
  const w = computeWinding(pb.edges);
  const isCW = w < 0;

  let aCount = 0,
    bCount = 0;
  for (const e of pb.edges) {
    if (e.side === 'A') aCount++;
    else if (e.side === 'B') bCount++;
  }

  // If geometry is CCW but most edges claim A → semantics inverted.
  if (!isCW && aCount >= bCount) return true;

  // If geometry is CW but most edges claim B → semantics inverted.
  if (isCW && bCount > aCount) return true;

  return false;
}

/********************************************************************************************
 *  FINALIZE PARTITION — FULL VERSION WITH REMAP + INVERSION FIX
 ********************************************************************************************/
export function finalizePartitionCore(prev: IBuildingProviderContext, globalIndex: number, unitType: EUnitType = EUnitType.METRIC): IBuildingProviderContext {
  // console.log('[FINALIZE:start]', { globalIndex });

  const part = prev.walls?.[globalIndex];
  if (!(part instanceof PartitionWallModel)) {
    // console.log('[FINALIZE:abort_not_partition]');
    return prev;
  }

  /********************************************************************************************
   * STEP 1 — FIND THE TWO WALL INTERSECTIONS
   ********************************************************************************************/
  // console.log('[FINALIZE:scan_rooms]');

  let hitRoom = -1,
    hitSpace = -1,
    hitBoundary = -1;
  let hitA: any = null,
    hitB: any = null;

  const spaceRefs: Array<{ space: ISpace; rI: number; sI: number }> = [];
  (prev.rooms ?? []).forEach((room, rI) => {
    (room.spaces ?? []).forEach((space: ISpace, sI) => {
      spaceRefs.push({ space, rI, sI });
    });
  });

  const assignedSpaceRefs = spaceRefs.filter(({ space }) => (space.walls ?? []).some((wall) => wall?.uuid === part.uuid));
  const scanSpaceRefs = assignedSpaceRefs.length > 0 ? assignedSpaceRefs : spaceRefs;

  const found = scanSpaceRefs.some(({ space, rI, sI }) =>
    (space.boundaries ?? []).some((b, bI) => {
      if (!(b instanceof PhysicalBoundaryModel)) return false;

      const splitTarget = findPartitionBoundaryHitPair(part.startPoint, part.endPoint, b);

      if (splitTarget) {
        hitA = splitTarget.hitA;
        hitB = splitTarget.hitB;
        hitRoom = rI;
        hitSpace = sI;
        hitBoundary = bI;

        // console.log('[FINALIZE:hits_found]', { hitA, hitB, hitRoom, hitSpace, hitBoundary });
        return true;
      }

      return false;
    }),
  );

  if (!found || hitRoom < 0) {
    // console.log('[FINALIZE:abort_no_hits]');
    return prev;
  }

  /********************************************************************************************
   * STEP 2 — REPLACE PARTITION WITH STANDARD WALL
   ********************************************************************************************/
  const toAttr = (a: any) =>
    a instanceof AttributeModel
      ? a
      : new AttributeModel(
          a.id,
          a.key,
          a.value,
          a.predecessorId,
          a.type,
          a.unit,
          a.hidden,
          a.forced,
          a.ownerId,
          a.userId,
          a.publicFlag,
          a.localization,
          a.defaults,
          a.validation,
          a.unitFamily,
        );

  const inheritedAttrs = (part.attributes ?? []).map(toAttr);
  // console.log(inheritedAttrs)
  const sw = new StandardWallModel(new THREE.Vector3(hitA.P.x, hitA.P.y, 0), new THREE.Vector3(hitB.P.x, hitB.P.y, 0), part.depth, [], [], [], []);

  sw.uuid = part.uuid;
  sw.attributes = updateWallGeometryAttributes(sw, unitType, inheritedAttrs);

  let next: IBuildingProviderContext = {
    ...prev,
    walls: [...(prev.walls ?? [])],
    rooms: [...(prev.rooms ?? [])],
  };
  next.walls[globalIndex] = sw;

  // console.log('[FINALIZE:partition_replaced]', { uuid: sw.uuid });

  /********************************************************************************************
   * STEP 2.5 — REMAP HITS TO CURRENT SEGMENTS (FIX)
   ********************************************************************************************/
  hitA = remapHitWallToCurrentSegments(next, hitA);
  hitB = remapHitWallToCurrentSegments(next, hitB);

  /********************************************************************************************
   * STEP 3 — SPLIT THE TWO WALLS
   ********************************************************************************************/
  next = splitWallInBuildingHelper(next, hitA.wall, [hitA.t]);
  next = rewireAllSpaceWalls(next);
  // console.log('[FINALIZE:after_split_A]');

  next = splitWallInBuildingHelper(next, hitB.wall, [hitB.t]);
  next = rewireAllSpaceWalls(next);
  // console.log('[FINALIZE:after_split_B]');

  /********************************************************************************************
   * STEP 4 — ACCESS MODIFIED BOUNDARY
   ********************************************************************************************/
  const room = next.rooms[hitRoom];
  const spaces = room.spaces.slice();
  room.spaces = spaces;

  const space = spaces[hitSpace];
  const boundaries = space.boundaries.slice();
  space.boundaries = boundaries;

  let pb = boundaries[hitBoundary] as PhysicalBoundaryModel;

  /********************************************************************************************
   * STEP 5 — REPAIR RING
   ********************************************************************************************/
  pb = repairPhysicalBoundaryRing(pb);

  // console.log('[FINALIZE:ring_repaired]', { edgeCount: pb.edges.length });

  /********************************************************************************************
   * STEP 6 — FIND INCOMING EDGE INDICES
   ********************************************************************************************/
  const P_A = new THREE.Vector3(hitA.P.x, hitA.P.y, 0);
  const P_B = new THREE.Vector3(hitB.P.x, hitB.P.y, 0);

  const iA = findIncomingEdgeIndexAtPoint(pb, P_A);
  const iB = findIncomingEdgeIndexAtPoint(pb, P_B);

  // console.log('[FINALIZE:incoming_indices]', { iA, iB });

  if (iA < 0 || iB < 0) {
    // console.log('[FINALIZE:abort_missing_indices]');
    return next;
  }

  /********************************************************************************************
   * STEP 7 — BUILD LOOPS
   ********************************************************************************************/
  const ring = pb.edges.slice();
  const n = ring.length;

  const path1 = walkRingInclusive(ring, (iA + 1) % n, iB);
  const path2 = walkRingInclusive(ring, (iB + 1) % n, iA);

  // console.log('[FINALIZE:path_sizes]', { p1: path1.length, p2: path2.length });

  /********************************************************************************************
   * STEP 8 — INVERSION-AWARE CHORD CREATION (FIX)
   ********************************************************************************************/
  /********************************************************************************************
   * STEP 8 — GEOMETRIC A/B ASSIGNMENT (CORRECTED)
   ********************************************************************************************/

  // 1. Find a representative point for Loop 1 (Centroid of path1)
  let count = 0,
    sumX = 0,
    sumY = 0;
  for (const e of path1) {
    sumX += e.wall.startPoint.x + e.wall.endPoint.x;
    sumY += e.wall.startPoint.y + e.wall.endPoint.y;
    count += 2;
  }

  // Fallback: if path1 is empty (rare), pick a point slightly offset from partition
  if (count === 0) {
    const midX = (P_A.x + P_B.x) * 0.5;
    const midY = (P_A.y + P_B.y) * 0.5;
    // Just pick the midpoint, the cross product will be 0, handled by default below
    sumX = midX;
    sumY = midY;
    count = 1;
  }

  const pX = sumX / count;
  const pY = sumY / count;

  // 2. Cross Product Check (2D)
  // Wall Vector U = P_A -> P_B
  const ux = P_B.x - P_A.x;
  const uy = P_B.y - P_A.y;

  // Test Vector V = P_A -> Loop Point
  const vx = pX - P_A.x;
  const vy = pY - P_A.y;

  // Cross Product: (Ux * Vy) - (Uy * Vx)
  const val = ux * vy - uy * vx;

  // 3. Determine Side
  // In a Y-Down coordinate system (like HTML Canvas/Three.js 2D views):
  // val > 0 means the point is to the RIGHT (Side B).
  // val < 0 means the point is to the LEFT (Side A).

  const isRightSide = val > 0;

  // If Loop 1 is on the Right (Side B), chordB must represent Side B.
  const sideForChordB = isRightSide ? 'B' : 'A';

  // Loop 2 is on the opposite side
  const sideForChordA = isRightSide ? 'A' : 'B';

  // console.log('[FINALIZE:geometric_side]', { val, isRightSide, sideForChordB });

  // chordB closes loop1 (Reversed: Pb->Pa)
  const chordB = new BoundaryEdge(sw, 0, 1, sideForChordB, true);

  // chordA closes loop2 (Forward: Pa->Pb)
  const chordA = new BoundaryEdge(sw, 0, 1, sideForChordA, false);

  const loop1 = [...path1, chordB];
  const loop2 = [...path2, chordA];

  /* console.log('[FINALIZE:loop_sizes]', {
    loop1: loop1.length,
    loop2: loop2.length,
  }); */

  /********************************************************************************************
   * STEP 10 — BUILD TWO NEW ROOMS
   ********************************************************************************************/
  const b1 = boundaryFromEdges(loop1);
  const b2 = boundaryFromEdges(loop2);

  // FIX: Wände für die neuen Räume direkt aus den aufgeteilten Loops extrahieren!
  const walls1 = dedupeWallsFromEdges(loop1);
  const walls2 = dedupeWallsFromEdges(loop2);

  const roomAttrs = room.attributes ?? [];
  const baseTitle = room.title?.trim() || '';
  const title1 = baseTitle;
  const title2 = baseTitle ? baseTitle + '-1' : '';

  const originalFloor = (space as any).floor;

  // --- Create floor #1 ---
  const floor1 = originalFloor
    ? new FloorModel(originalFloor.tiles, originalFloor.texture ? { ...originalFloor.texture } : undefined, originalFloor.attributes?.map((a: any) => ({ ...a })) ?? [])
    : new FloorModel();

  // --- Create floor #2 (same source, but fresh instance) ---
  const floor2 = originalFloor
    ? new FloorModel(originalFloor.tiles, originalFloor.texture ? { ...originalFloor.texture } : undefined, originalFloor.attributes?.map((a: any) => ({ ...a })) ?? [])
    : new FloorModel();

  const room1 = new RoomModel(
    title1,
    [
      new SpaceModel(
        floor1, // Single FloorModel
        space.products,
        space.elements,
        space.models2D,
        [b1],
        walls1, // FIX: Nur lokale Wände übergeben!
      ),
    ],
    roomAttrs.slice(),
  );

  const room2 = new RoomModel(
    title2,
    [
      new SpaceModel(
        floor2, // Single FloorModel
        [], // No products
        [], // No elements
        [], // No 2D models
        [b2],
        walls2, // FIX: Nur lokale Wände übergeben!
      ),
    ],
    roomAttrs.slice(),
  );

  next.rooms[hitRoom] = room1;
  next.rooms.splice(hitRoom + 1, 0, room2);

  next = rewireAllSpaceWalls(next);

  // console.log('[FINALIZE:done]');
  return {
    ...next,
    isSetByUndoRedo: false,
    lastAction: EHistoryAction.WALLS_CHANGED,
  };
}

/**
 * Removes a wall from the building and drops any boundary edges that reference it.
 * Preserves UUIDs when cloning rooms/spaces.
 */
export function removeWallFromBuildingHelper(prev: IBuildingProviderContext, wallUuid: string): IBuildingProviderContext {
  const next: IBuildingProviderContext = { ...prev };
  next.walls = (prev.walls ?? []).filter((w) => w && w.uuid !== wallUuid);
  if (prev.rooms) {
    next.rooms = prev.rooms.map((room) => {
      const newRoom = new RoomModel(room.title ?? '', room.spaces.slice(), room.attributes ?? []);
      if ((room as RoomModel).uuid) newRoom.setUUID((room as RoomModel).uuid); // preserve Room UUID
      newRoom.spaces = room.spaces.map((space: ISpace) => {
        const newSpace = new SpaceModel(space.floor, space.products, space.elements, space.models2D, space.boundaries.slice(), next.walls);
        if ((space as SpaceModel).uuid) (newSpace as SpaceModel).setUUID((space as SpaceModel).uuid); // preserve Space UUID
        newSpace.boundaries = space.boundaries.map((b) => {
          if (!(b instanceof PhysicalBoundaryModel)) return b;
          const newEdges = b.edges.filter((e) => e?.wall?.uuid !== wallUuid);
          const newWalls = b.walls.filter((w) => w && w.uuid !== wallUuid);
          return new PhysicalBoundaryModel(newWalls, newEdges);
        });
        return newSpace;
      });
      return newRoom;
    });
  }
  return next;
}
